import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  DialogContentText,
  CircularProgress,
} from "@mui/material";
import { AnalysisType } from "@app/types/samples.types";

interface CopyAnalysisDataModalProps {
  open: boolean;
  onClose: () => void;
  currentAnalysisType: AnalysisType;
  availableAnalysisTypes: AnalysisType[];
  onCopy: (targetAnalysisType: AnalysisType) => Promise<void>;
}

const CopyAnalysisDataModal: React.FC<CopyAnalysisDataModalProps> = ({
  open,
  onClose,
  currentAnalysisType,
  availableAnalysisTypes,
  onCopy,
}) => {
  const [selectedAnalysisType, setSelectedAnalysisType] =
    useState<AnalysisType | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copying, setCopying] = useState(false);

  const handleSelectAnalysisType = (analysisType: AnalysisType) => {
    setSelectedAnalysisType(analysisType);
    setConfirmOpen(true);
  };

  const handleConfirmCopy = async () => {
    if (!selectedAnalysisType) return;

    setCopying(true);
    try {
      await onCopy(selectedAnalysisType);
      setConfirmOpen(false);
      onClose();
      setSelectedAnalysisType(null);
    } catch (error) {
      console.error("Error al copiar datos:", error);
    } finally {
      setCopying(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setSelectedAnalysisType(null);
  };

  const handleClose = () => {
    if (!copying) {
      onClose();
      setSelectedAnalysisType(null);
    }
  };

  // Filtrar el tipo de análisis actual de la lista
  const filteredAnalysisTypes = availableAnalysisTypes.filter(
    (type) => type.id !== currentAnalysisType.id
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Copiar datos de análisis</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleccione el tipo de análisis al que desea copiar los datos desde{" "}
            <strong>{currentAnalysisType.descripcion}</strong>:
          </Typography>

          {filteredAnalysisTypes.length === 0 ? (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No hay otros tipos de análisis disponibles para esta muestra.
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredAnalysisTypes.map((analysisType) => (
                <ListItem key={analysisType.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelectAnalysisType(analysisType)}
                  >
                    <ListItemText
                      primary={analysisType.descripcion}
                      secondary={`Código: ${analysisType.codigo}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={copying}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={handleCancelConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar copia de datos</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea copiar los datos del análisis "
            <strong>{currentAnalysisType.descripcion}</strong>" al análisis "
            <strong>{selectedAnalysisType?.descripcion}</strong>"?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: "error.main" }}>
            Esta operación no es reversible y sobrescribirá los datos existentes
            que sean compatibles.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm} disabled={copying}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmCopy}
            variant="contained"
            color="primary"
            disabled={copying}
            startIcon={copying ? <CircularProgress size={20} /> : null}
          >
            {copying ? "Copiando..." : "Copiar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CopyAnalysisDataModal;
